import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cognitoRefreshToken } from '../services/api';

const AuthContext = createContext(null);
const TOKENS_KEY = 'owOwnerTokens';
const EMAIL_KEY = 'owOwnerEmail';
// UI hint only (set when logging in through /admin); the backend enforces it.
const SUPER_ADMIN_KEY = 'owOwnerIsSuperAdmin';

const readStoredSuperAdmin = () => localStorage.getItem(SUPER_ADMIN_KEY) === 'true';

const decodeJwt = (token) => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

const msUntilExpiry = (accessToken) => {
  const payload = decodeJwt(accessToken);
  if (!payload?.exp) return -1;
  return payload.exp * 1000 - Date.now();
};

const stripRefreshToken = ({ RefreshToken, ...rest } = {}) => rest;

const readStoredTokens = () => {
  try {
    const stored = localStorage.getItem(TOKENS_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const refreshTimerRef = useRef(null);
  const [tokens, setTokens] = useState(readStoredTokens);
  const [email, setEmail] = useState(() => localStorage.getItem(EMAIL_KEY) || null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(readStoredSuperAdmin);
  const tokensRef = useRef(tokens);
  const emailRef = useRef(email);
  const isSuperAdminRef = useRef(isSuperAdmin);
  tokensRef.current = tokens;
  emailRef.current = email;
  isSuperAdminRef.current = isSuperAdmin;

  const isAuthenticated = !!tokens;

  const logout = useCallback(() => {
    clearTimeout(refreshTimerRef.current);
    const wasSuperAdmin = isSuperAdminRef.current;
    setTokens(null);
    setEmail(null);
    setIsSuperAdmin(false);
    localStorage.removeItem(TOKENS_KEY);
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(SUPER_ADMIN_KEY);
    navigate(wasSuperAdmin ? '/admin/login' : '/restaurant/login');
  }, [navigate]);

  const refreshTokens = useCallback(async (userEmail) => {
    try {
      // No refresh_token in the body: the browser sends the ow_refresh_token
      // HttpOnly cookie automatically (credentials: 'include' in api.js).
      const data = await cognitoRefreshToken(userEmail);
      const newTokens = stripRefreshToken(data);
      setTokens(newTokens);
      localStorage.setItem(TOKENS_KEY, JSON.stringify(newTokens));
      return newTokens;
    } catch {
      logout();
      return null;
    }
  }, [logout]);

  const scheduleRefresh = useCallback((currentTokens) => {
    clearTimeout(refreshTimerRef.current);
    if (!currentTokens?.AccessToken) return;

    const delay = msUntilExpiry(currentTokens.AccessToken) - 60_000;
    const run = () => refreshTokens(emailRef.current);
    if (delay <= 0) {
      run();
      return;
    }
    refreshTimerRef.current = setTimeout(run, delay);
  }, [refreshTokens]);

  const login = (authResult, userEmail, { isSuperAdmin: superAdmin = false } = {}) => {
    // RefreshToken now lives only in the HttpOnly ow_refresh_token cookie,
    // never in JS-reachable storage.
    const tokensToStore = stripRefreshToken(authResult);
    setTokens(tokensToStore);
    setEmail(userEmail);
    setIsSuperAdmin(superAdmin);
    localStorage.setItem(TOKENS_KEY, JSON.stringify(tokensToStore));
    localStorage.setItem(EMAIL_KEY, userEmail);
    localStorage.setItem(SUPER_ADMIN_KEY, String(superAdmin));
  };

  const authenticatedFetch = useCallback(async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${tokensRef.current?.AccessToken}`,
      },
    });

    if (response.status === 401) {
      logout();
      throw new Error('Session expired. Please login again.');
    }

    return response;
  }, [logout]);

  useEffect(() => {
    if (tokens?.AccessToken) scheduleRefresh(tokens);
    return () => clearTimeout(refreshTimerRef.current);
  }, [tokens, scheduleRefresh]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== TOKENS_KEY && event.key !== EMAIL_KEY && event.key !== SUPER_ADMIN_KEY) return;
      if (!localStorage.getItem(TOKENS_KEY)) {
        clearTimeout(refreshTimerRef.current);
        const wasSuperAdmin = isSuperAdminRef.current;
        setTokens(null);
        setEmail(null);
        setIsSuperAdmin(false);
        navigate(wasSuperAdmin ? '/admin/login' : '/restaurant/login');
        return;
      }
      setTokens(readStoredTokens());
      setEmail(localStorage.getItem(EMAIL_KEY) || null);
      setIsSuperAdmin(readStoredSuperAdmin());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isSuperAdmin, tokens, email, login, logout, authenticatedFetch }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
